import React from "react";
import { useTranslation } from "react-i18next";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/solid";
import { UserIcon } from "@heroicons/react/24/outline";
import { focusRing } from "../Exams/TestBuilder/ui";
import { isOpenClick } from "../../utils/rowLink";

// One calm grey for every expert's avatar: colour on these screens means a vote or a status, nothing else.
const AVATAR_TONE = "bg-slate-100 text-slate-700 ring-slate-600/15";

const nameOf = (evaluator) => (evaluator?.full_name || "").trim();

// The first letters of the first two words, as in the expert's dialog.
const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();

function Avatar({ evaluator, size = "h-9 w-9" }) {
  const name = nameOf(evaluator);
  return (
    <span
      aria-hidden="true"
      className={`flex ${size} shrink-0 items-center justify-center rounded-full text-xs font-semibold ring-1 ring-inset ${AVATAR_TONE}`}
    >
      {name ? initials(name) : <UserIcon className="h-4 w-4" />}
    </span>
  );
}

// The expert's name opens the dialog with their reviews; an account without a name says so.
function NameButton({ evaluator, onOpen, className = "" }) {
  const [t] = useTranslation();
  const name = nameOf(evaluator);
  return (
    <button
      type="button"
      onClick={() => onOpen(evaluator)}
      title={t("evaluators.table.view_questions")}
      className={`rounded text-left transition-colors duration-200 hover:text-blue-700 ${focusRing} ${
        name ? "text-slate-900" : "italic text-slate-500"
      } ${className}`}
    >
      {name || t("evaluators.no_name")}
    </button>
  );
}

// The main subject as a muted line under the name: most accounts have none, so it gets no column. It wraps
// rather than truncates, so a long subject does not hold the table's first column wide.
// (relative: the sr-only text is positioned absolutely and must stay inside the table's scroll box.)
function SubjectLine({ subject, className = "" }) {
  const [t] = useTranslation();
  if (!subject) return null;
  return (
    <p className={`relative break-words text-xs text-slate-500 ${className}`}>
      <span className="sr-only">{t("evaluators.table.main_subject")}: </span>
      {subject}
    </p>
  );
}

// The up or down votes an expert gave: the colour is the thumb's only (no pill), so a long list stays calm; a zero
// is grey. `label` names the number where no column header does (the cards).
function VoteCount({ value, up = false, label }) {
  const Icon = up ? HandThumbUpIcon : HandThumbDownIcon;
  const count = value || 0;
  const thumb = !count ? "text-slate-300" : up ? "text-emerald-600" : "text-red-500";
  return (
    <span
      title={label}
      className={`relative inline-flex items-center gap-1.5 text-sm font-medium tabular-nums ${count ? "text-slate-900" : "text-slate-500"}`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${thumb}`} aria-hidden="true" />
      {label ? (
        <>
          <span className="sr-only">{label}</span>
          <span aria-hidden="true">{count}</span>
        </>
      ) : (
        count
      )}
    </span>
  );
}

const headerCell = "whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500";

/**
 * The experts as a table (wide screens): the expert (avatar, name, main subject), their reviews and how many
 * of them were up and down. A click on a row or on the name opens `onViewEvaluator(evaluator)`. `sortedBy`
 * ({column, direction}) marks the sorted column for screen readers.
 */
export default function Table({ evaluators, onViewEvaluator, sortedBy }) {
  const [t] = useTranslation();
  // The number columns keep one width on wide screens; narrower ones (the sidebar still shown) shrink them.
  const columns = [
    { id: "evaluator", label: t("evaluators.table.evaluator"), className: "text-left" },
    { id: "reviews", label: t("evaluators.table.reviews"), className: "text-center xl:w-36" },
    { id: "upvotes", label: t("evaluators.table.upvotes"), className: "text-center xl:w-36" },
    { id: "downvotes", label: t("evaluators.table.downvotes"), className: "text-center xl:w-36" },
  ];

  return (
    // relative: the sr-only texts in the cells stay inside this scroll box instead of widening the page.
    <div className="relative overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                aria-sort={sortedBy?.column === column.id ? sortedBy.direction : undefined}
                className={`${headerCell} ${column.className}`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {evaluators.map((evaluator) => (
            <tr
              key={evaluator.uuid}
              onClick={(event) => isOpenClick(event) && onViewEvaluator(evaluator)}
              className="cursor-pointer transition-colors duration-150 focus-within:bg-slate-50/80 hover:bg-slate-50/80"
            >
              <td className="px-4 py-3">
                <div className="flex min-w-[12rem] items-center gap-3">
                  <Avatar evaluator={evaluator} />
                  <div className="min-w-0">
                    <NameButton evaluator={evaluator} onOpen={onViewEvaluator} className="text-sm font-medium" />
                    <SubjectLine subject={evaluator.domain_subject} className="mt-0.5" />
                  </div>
                </div>
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-center text-sm font-semibold tabular-nums text-slate-900">
                {evaluator.total_reviews || 0}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-center">
                <VoteCount up value={evaluator.upvotes} />
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-center">
                <VoteCount value={evaluator.downvotes} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The experts on phones, one card each with the table's information: the name (a button that opens
 * `onViewEvaluator(evaluator)`, as does a tap on the card), the main subject, the reviews and the up and
 * down votes.
 */
export function EvaluatorCards({ evaluators, onViewEvaluator }) {
  const [t] = useTranslation();
  return (
    <ul className="flex flex-col gap-2.5">
      {evaluators.map((evaluator) => (
        <li
          key={evaluator.uuid}
          onClick={(event) => isOpenClick(event) && onViewEvaluator(evaluator)}
          className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm transition duration-200 hover:border-slate-300"
        >
          <div className="flex items-start gap-3">
            <Avatar evaluator={evaluator} size="h-10 w-10" />
            <div className="min-w-0 flex-1">
              <NameButton
                evaluator={evaluator}
                onOpen={onViewEvaluator}
                className="text-[15px] font-semibold leading-snug"
              />
              <SubjectLine subject={evaluator.domain_subject} className="mt-0.5" />
              <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <span className="text-sm font-medium tabular-nums text-slate-700">
                  {t("evaluators.list.reviews", { count: evaluator.total_reviews || 0 })}
                </span>
                <VoteCount up value={evaluator.upvotes} label={`${t("evaluators.table.upvotes")}: ${evaluator.upvotes || 0}`} />
                <VoteCount value={evaluator.downvotes} label={`${t("evaluators.table.downvotes")}: ${evaluator.downvotes || 0}`} />
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
